import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/common/Layout';
import UserModal from '../components/common/UserModal';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import type { Organization, UserResponse, User } from '../types';
import Swal from 'sweetalert2';

const OrganizationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userResponses, setUserResponses] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingUserId, setDownloadingUserId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [originalOrganizationName, setOriginalOrganizationName] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  // Redirect non-admin users
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
  }, [user, navigate]);

  // Fetch organization data
  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const orgId = parseInt(id);
        
        // Get all organizations and find the specific one
        const organizations = await apiService.getAllOrganizations();
        const foundOrg = organizations.find(org => org.id === orgId);
        
        if (!foundOrg) {
          setError('Organization not found');
          setLoading(false);
          return;
        }

        setOrganization(foundOrg);
        setOrganizationName(foundOrg.name);
        setOriginalOrganizationName(foundOrg.name);

        // Fetch user responses for this organization
        try {
          const responses = await apiService.getOrganizationResponses(orgId);
          setUserResponses(Array.isArray(responses) ? responses : []);
        } catch (responseErr) {
          console.warn('Failed to fetch organization responses, trying individual user responses');
          // Fallback: fetch individual user responses
          const allResponses: UserResponse[] = [];
          for (const user of foundOrg.users) {
            try {
              const userResponsesData = await apiService.getUserResponses(user.id);
              if (Array.isArray(userResponsesData)) {
                allResponses.push(...userResponsesData);
              }
            } catch (userErr) {
              console.warn(`Failed to fetch responses for user ${user.id}`);
            }
          }
          setUserResponses(allResponses);
        }

        setError(null);
      } catch (err) {
        console.error('Failed to fetch organization:', err);
        setError('Failed to load organization details');
        setUserResponses([]); // Ensure userResponses is always an array
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizationData();
  }, [id]);

  // Generate user avatar colors
  const getUserAvatarColor = (userId: number): string => {
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    return colors[userId % colors.length];
  };

  // Get user initials
  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Get active (non-soft-deleted) users
  const getActiveUsers = (): User[] => {
    return organization?.users?.filter(user => !user.deleted_at) || [];
  };

  // Calculate average completion for the organization
  const getOrganizationAverageCompletion = (): string => {
    const activeUsers = getActiveUsers();
    if (!activeUsers.length || !organization?.templates?.length) {
      return '0%';
    }

    // Ensure userResponses is always an array
    const responses = Array.isArray(userResponses) ? userResponses : [];

    let totalPercentage = 0;
    let userCount = 0;

    // Calculate completion percentage for each active user across all org templates
    for (const user of activeUsers) {
      let userTotalQuestions = 0;
      let userCompletedQuestions = 0;

      // Get completion for each template assigned to this organization
      for (const template of organization.templates) {
        const templateQuestions = template.questions?.length || 0;
        userTotalQuestions += templateQuestions;

        // Get unique question IDs that this user has responded to for this template
        const userTemplateResponses = responses.filter(
          response => response.user_id === user.id && response.template_id === template.id
        );
        const questionsWithResponses = new Set(
          userTemplateResponses.map(response => response.question_id)
        );
        userCompletedQuestions += questionsWithResponses.size;
      }

      // Calculate user's overall completion percentage
      const userPercentage = userTotalQuestions > 0 
        ? (userCompletedQuestions / userTotalQuestions) * 100 
        : 0;
      
      totalPercentage += userPercentage;
      userCount++;
    }

    // Return average completion percentage across all users
    const averagePercentage = userCount > 0 ? totalPercentage / userCount : 0;
    return `${Math.round(averagePercentage)}%`;
  };

  // Calculate user completion
  const getUserCompletion = (user: User) => {
    if (!organization?.templates.length) {
      return { percentage: 0, completed: 0, total: 0 };
    }

    // Ensure userResponses is always an array
    const responses = Array.isArray(userResponses) ? userResponses : [];

    let totalQuestions = 0;
    let completedQuestions = 0;

    // Calculate across all organization templates
    for (const template of organization.templates) {
      const templateQuestions = template.questions?.length || 0;
      totalQuestions += templateQuestions;

      // Get unique question IDs that this user has responded to for this template
      const userTemplateResponses = responses.filter(
        response => response.user_id === user.id && response.template_id === template.id
      );
      const questionsWithResponses = new Set(
        userTemplateResponses.map(response => response.question_id)
      );
      completedQuestions += questionsWithResponses.size;
    }

    const percentage = totalQuestions > 0 ? Math.round((completedQuestions / totalQuestions) * 100) : 0;
    return { percentage, completed: completedQuestions, total: totalQuestions };
  };

  // Get user's last active date based on their latest response or account creation
  const getUserLastActiveDate = (user: User): string => {
    // Ensure userResponses is always an array
    const responses = Array.isArray(userResponses) ? userResponses : [];
    
    // Find all responses for this user
    const userResponsesData = responses.filter(response => response.user_id === user.id);

    if (userResponsesData.length > 0) {
      // Find the latest response by updated_at date
      const latestResponse = userResponsesData.reduce((latest, current) => {
        const latestDate = new Date(latest.updated_at);
        const currentDate = new Date(current.updated_at);
        return currentDate > latestDate ? current : latest;
      });
      
      return new Date(latestResponse.updated_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } else {
      // No responses yet, use user's created_at date
      return new Date(user?.created_at || '2023-01-01').toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  // Handle PDF download for user
  const handleDownloadUserPdf = async (userId: number) => {
    if (!organization?.templates.length) {
      toast.error('No worksheets available for this user');
      return;
    }

    try {
      setDownloadingUserId(userId);
      // // For now, download the first template's PDF
      // const templateId = organization.templates[0].id;
      // const pdfBlob = await apiService.downloadTemplatePdf(templateId);
      
      // // Create download link
      // const url = window.URL.createObjectURL(pdfBlob);
      // const link = document.createElement('a');
      // link.href = url;
      // link.download = `user-${userId}-responses.pdf`;
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);
      // window.URL.revokeObjectURL(url);
      
      toast.success('Under development: PDF download feature coming soon!');
    } catch (err) {
      console.error('Failed to download PDF:', err);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingUserId(null);
    }
  };

  // Handle saving organization name changes
  const handleSaveOrganizationName = async () => {
    console.log('Saving organization name:', organizationName);
    if (!organization || !organizationName.trim()) {
      toast.error('Organization name cannot be empty');
      // setOrganizationName(originalOrganizationName);
      // setEditingName(false);
      return;
    }

    console.log('Original organization name:', originalOrganizationName);
    if (organizationName.trim() === originalOrganizationName) {
      setEditingName(false);
      return;
    }

    try {
      console.log('Updating organization name to:', organizationName.trim());
      const updatedOrg = await apiService.updateOrganization(organization.id, { name: organizationName.trim() });
      
      console.log('Updated organization:', updatedOrg);
      // Preserve existing organization data and only update the name
      setOrganization(prevOrg => ({
        ...prevOrg!,
        name: organizationName.trim(),
        updated_at: updatedOrg.updated_at || prevOrg!.updated_at
      }));
      setOriginalOrganizationName(organizationName.trim());
      setEditingName(false);
      toast.success('Organization name updated successfully! 🎉');
    } catch (err) {
      console.error('Failed to update organization name:', err);
      toast.error('Failed to update organization name. Please try again.');
      setOrganizationName(originalOrganizationName);
      setEditingName(false);
    }
  };

  const handleCancelEdit = () => {
    setOrganizationName(originalOrganizationName);
    setEditingName(false);
  };

  const handleUserModal = async (userData: {
    name: string;
    email: string;
    jobTitle?: string;
    organizationId: number;
    password?: string;
    autoGenerate: boolean;
    sendWelcomeEmail: boolean;
  }) => {
    try {
      setIsAddingUser(true);
      
      if (editingUser) {
        // Update existing user
        await apiService.updateUser(editingUser.id, {
          name: userData.name,
          email: userData.email,
          jobTitle: userData.jobTitle,
          password: userData.password || undefined,
        });
        
        toast.success(`Successfully updated "${userData.name}"`);
      } else {
        // Create new user
        await apiService.createUser({
          name: userData.name,
          email: userData.email,
          jobTitle: userData.jobTitle,
          organizationId: userData.organizationId,
          password: userData.password!,
          sendWelcomeEmail: userData.sendWelcomeEmail,
        });
        
        const welcomeMessage = userData.sendWelcomeEmail 
          ? ' and welcome email sent' 
          : '';
        
        const passwordMessage = userData.autoGenerate 
          ? ' with auto-generated password' 
          : '';
        
        toast.success(`Successfully added "${userData.name}"${passwordMessage}${welcomeMessage}`);
      }
      
      // Refresh the organization data
      if (id) {
        const orgId = parseInt(id);
        const organizations = await apiService.getAllOrganizations();
        const updatedOrg = organizations.find(org => org.id === orgId);
        if (updatedOrg) {
          setOrganization(updatedOrg);
        }
      }
      
      // Close modal and reset state
      setShowUserModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error(`Failed to ${editingUser ? 'update' : 'add'} user:`, err);
      toast.error(`Failed to ${editingUser ? 'update' : 'add'} user. ${err}`);
    } finally {
      setIsAddingUser(false);
    }
  };

  const handleCloseUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = async (user: User) => {
    // Show SweetAlert confirmation dialog
    const result = await Swal.fire({
      title: 'Remove User?',
      html: `Remove <strong>${user.name}</strong> (<em>${user.email}</em>) from <strong>${organization?.name || 'this organization'}</strong>?<br><br>User cannot log in but responses will be preserved.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Remove User',
      cancelButtonText: 'Cancel',
      focusCancel: true,
      customClass: {
        confirmButton: 'swal2-confirm-delete',
        cancelButton: 'swal2-cancel'
      }
    });
    
    if (!result.isConfirmed) return;
    
    try {
      setDeletingUserId(user.id);
      
      // Call the delete API
      await apiService.deleteUser(user.id);
      
      toast.success(`Successfully deleted "${user.name}"`);
      
      // Refresh the organization data
      if (id) {
        const orgId = parseInt(id);
        const organizations = await apiService.getAllOrganizations();
        const updatedOrg = organizations.find(org => org.id === orgId);
        if (updatedOrg) {
          setOrganization(updatedOrg);
        }
      }
      
    } catch (err) {
      console.error('Failed to delete user:', err);
      
      // Show error message with SweetAlert
      await Swal.fire({
        title: 'Error!',
        text: 'Failed to delete user. Please try again.',
        icon: 'error',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setDeletingUserId(null);
    }
  };

  const underDevelopmentFeature = () => {
    Swal.fire({
      title: 'Under Development',
      text: 'The feature to view all user responses for this template is under development and will be available soon.',
      icon: 'info',
      confirmButtonText: 'OK'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading organization details...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !organization) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Organization Not Found</h2>
            <p className="text-gray-600 mb-4">{error || 'The requested organization could not be found.'}</p>
            <Link
              to="/dashboard"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/dashboard"
                className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-800"
              >
                Dashboard
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">{organization.name}</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Organization Header */}
        <div className="mb-8">
          <div className="flex items-start space-x-6">
            {/* Organization Icon */}
            <div className="flex-shrink-0">
              <span className="text-6xl">🏢</span>
            </div>
            
            {/* Organization Details */}
            <div className="flex-1">
              {/* Editable Organization Name */}
              <div className="flex items-center mb-2">
                {editingName ? (
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full">
                    <input
                      type="text"
                      value={organizationName}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveOrganizationName();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit();
                        }
                      }}
                      onChange={(e) => setOrganizationName(e.target.value)}
                      className="text-2xl sm:text-3xl font-bold text-gray-900 bg-transparent border-2 border-primary-500 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full sm:min-w-[400px] sm:w-auto"
                    />
                    <div className="flex space-x-2 justify-center sm:justify-start">
                      <button
                        onClick={handleSaveOrganizationName}
                        className="text-green-600 hover:text-green-700 focus:outline-none"
                        title="Save changes"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="text-red-600 hover:text-red-700 focus:outline-none"
                        title="Cancel changes"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <h1 
                    className="text-3xl font-bold text-gray-900 hover:text-primary-600 cursor-pointer transition-colors duration-200 flex items-center group"
                    onClick={() => setEditingName(true)}
                    title="Click to edit organization name"
                  >
                    {organization.name}
                    <svg className="w-5 h-5 ml-2 text-gray-400 group-hover:text-primary-600 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </h1>
                )}
              </div>
              
              {/* Organization Stats */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                  <span className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">{getActiveUsers().length}</span> {getActiveUsers().length === 1 ? 'User' : 'Users'}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                  <span className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">{organization?.templates?.length || 0}</span> {(organization?.templates?.length || 0) === 1 ? 'Worksheet' : 'Worksheets'}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                  <span className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">{getOrganizationAverageCompletion()}</span> avg completion
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between pb-4 border-b-2 border-gray-300 mb-6">
            <div className="flex items-center">
              <h2 className="text-2xl font-bold text-gray-900 mr-2">Users</h2>
              <div className="w-8 h-8 min-w-8 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                {getActiveUsers().length}
              </div>
            </div>
            <button 
              onClick={() => setShowUserModal(true)}
              className="inline-flex items-center px-4 py-2 border-2 border-secondary text-sm font-medium rounded-md text-gray-800 bg-white hover:bg-secondary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add User
            </button>
          </div>

          {getActiveUsers().length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No users in this organization.</p>
            </div>
          ) : (
            <div className="space-y-0 border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
              {getActiveUsers().map((user, index) => {
                const completion = getUserCompletion(user);
                const avatarColor = getUserAvatarColor(user.id);
                const userInitials = getUserInitials(user.name);
                
                return (
                  <div 
                    key={user.id} 
                    className={`bg-white p-6 flex items-center justify-between min-w-[800px] ${
                      index !== (getActiveUsers().length - 1) ? 'border-b border-gray-200' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      {/* User Avatar */}
                      <div className={`w-12 h-12 ${avatarColor} rounded-full flex items-center justify-center text-white font-semibold`}>
                        {userInitials}
                      </div>
                      
                      {/* User Details */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{user.name}</h3>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        {user.job_title && (
                          <p className="text-sm text-gray-500">
                            <span className="inline-flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 002 2h-4a2 2 0 002-2z" />
                              </svg>
                              {user.job_title}
                            </span>
                          </p>
                        )}
                        <p className="text-sm text-gray-500">
                          Last active: {getUserLastActiveDate(user)}
                        </p>
                      </div>
                    </div>

                    {/* Completion Details */}
                    <div className="flex items-center space-x-6">
                      <div className="text-center min-w-[200px]">
                        {/* Progress Bar */}
                        <div className="w-32 bg-gray-200 rounded-full h-2 mb-2 mx-auto">
                          <div 
                            className={`h-2 rounded-full ${
                              completion.percentage >= 80 ? 'bg-green-500' : 
                              completion.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${completion.percentage}%` }}
                          ></div>
                        </div>
                        
                        {/* Percentage */}
                        <p className="text-sm font-medium text-gray-900 mb-1">{completion.percentage}%</p>
                        
                        {/* Progress Details */}
                        <p className="text-xs text-gray-500">{completion.completed}/{completion.total}</p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col space-y-2">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate(`/organization/${organization.id}/user/${user.id}/responses`)}
                            className="inline-flex items-center justify-center w-36 px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-white bg-secondary hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            View Responses
                          </button>
                          
                          <button
                            onClick={() => handleDownloadUserPdf(user.id)}
                            disabled={downloadingUserId === user.id}
                            className="inline-flex items-center justify-center w-36 px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {downloadingUserId === user.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Downloading...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                PDF Download
                              </>
                            )}
                          </button>
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="inline-flex items-center justify-center w-36 px-3 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit User
                          </button>

                          <button
                            onClick={() => handleDeleteUser(user)}
                            disabled={deletingUserId === user.id}
                            className="inline-flex items-center justify-center w-36 px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {deletingUserId === user.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700 mr-2"></div>
                                Deleting...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete User
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Organization Templates Section */}
        <div>
          <div className="flex items-center pb-4 border-b-2 border-gray-300 mb-4">
            <h2 className="text-2xl font-bold text-gray-900 mr-2">Organization Worksheets</h2>
            <div className="w-8 h-8 min-w-8 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
              {organization?.templates?.length || 0}
            </div>
          </div>
          
          <p className="text-gray-600 mb-6">
            Worksheets assigned to this organization, typically duplicated from base worksheets.
          </p>

          {(organization?.templates?.length || 0) === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No worksheets assigned to this organization.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {organization?.templates?.map((template) => {
                
                return (
                  <div key={template.id} className={`bg-white border border-gray-200 rounded-lg shadow-sm border-l-4 flex relative`}>
                    <div className="flex-shrink-0 p-4 flex items-center justify-center bg-gray-50 w-16">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">{template.name}</h3>
                          <span className="inline-flex items-center px-3 py-1.5 rounded text-xs font-medium bg-purple-600 text-white">
                            Organization Worksheet
                          </span>
                        </div>
                      </div>
                    
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          <p>{template.questions?.length || 0} questions configured • Created: {new Date(template.created_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })} • {getActiveUsers().length} users assigned</p>
                        </div>
                      </div>

                      <div className="flex gap-3 mt-4">
                        <button 
                          className="inline-flex items-center px-4 py-2.5 border border-transparent text-base font-medium rounded-md text-white bg-secondary-600 hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                          onClick={() => navigate(`/edit-template/${template.id}`)}
                        >
                          Edit Template
                        </button>

                        <button 
                          className="inline-flex items-center px-4 py-2.5 border border-2 border-secondary-300 text-base font-medium rounded-md text-gray-800 bg-white hover:text-white hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                          onClick={() => underDevelopmentFeature()}
                        >
                          View All User Responses
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* User Modal (Add/Edit) */}
      {organization && (
        <UserModal
          isOpen={showUserModal}
          onClose={handleCloseUserModal}
          onConfirm={handleUserModal}
          organization={organization}
          user={editingUser}
          isLoading={isAddingUser}
        />
      )}
    </Layout>
  );
};

export default OrganizationDetails;